import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Bell, List, X, SignOut } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";

export function Wordmark({ className = "" }) {
    return (
        <Link to="/" className={`font-heading font-extrabold tracking-tightest text-2xl ${className}`} data-testid="wordmark-link">
            SocietyIntent
        </Link>
    );
}

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [notifs, setNotifs] = useState([]);
    const [showNotif, setShowNotif] = useState(false);
    const unread = notifs.filter((n) => !n.read).length;

    useEffect(() => {
        if (!user) return;
        api.get("/notifications").then((r) => setNotifs(r.data || [])).catch(() => {});
    }, [user]);

    const dashboardPath = user
        ? user.role === "admin"
            ? "/admin"
            : user.role === "advertiser"
                ? "/advertiser"
                : "/respondent"
        : "/login";

    const onLogout = async () => {
        await logout();
        navigate("/");
    };

    const markRead = async () => {
        await api.post("/notifications/read-all").catch(() => {});
        setNotifs((ns) => ns.map((n) => ({ ...n, read: true })));
    };

    return (
        <header className="sticky top-0 z-40 bg-paper/85 backdrop-blur-md border-b border-ink-200">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
                <Wordmark />
                <nav className="hidden md:flex items-center gap-8 text-sm">
                    <Link to="/#how" className="hover:text-respondent" data-testid="nav-how">How it works</Link>
                    <Link to="/for-advertisers" className="hover:text-advertiser" data-testid="nav-advertisers">For advertisers</Link>
                    <Link to="/faq" className="hover:underline" data-testid="nav-faq">FAQ</Link>
                </nav>
                <div className="flex items-center gap-3">
                    {user ? (
                        <>
                            <button
                                onClick={() => setShowNotif((s) => !s)}
                                className="relative btn btn-ghost px-3 py-2"
                                data-testid="notif-toggle-btn"
                                aria-label="Notifications"
                            >
                                <Bell size={20} weight="regular" />
                                {unread > 0 && (
                                    <span className="absolute top-1 right-1 w-2 h-2 bg-respondent" />
                                )}
                            </button>
                            <Link to={dashboardPath} className="btn btn-outline hidden sm:inline-flex" data-testid="nav-dashboard-btn">
                                Dashboard
                            </Link>
                            <button onClick={onLogout} className="btn btn-ghost" data-testid="nav-logout-btn">
                                <SignOut size={18} />
                                <span className="hidden sm:inline">Sign out</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="btn btn-ghost hidden sm:inline-flex" data-testid="nav-login-btn">Log in</Link>
                            <Link to="/register" className="btn btn-ink" data-testid="nav-signup-btn">Join free</Link>
                        </>
                    )}
                    <button
                        className="md:hidden btn btn-ghost p-2"
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Menu"
                        data-testid="mobile-menu-btn"
                    >
                        {open ? <X size={20} /> : <List size={20} />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="md:hidden border-t border-ink-200 bg-white" data-testid="mobile-nav-menu">
                    <div className="px-6 py-4 flex flex-col gap-3 text-sm">
                        <Link to="/#how" onClick={() => setOpen(false)}>How it works</Link>
                        <Link to="/for-advertisers" onClick={() => setOpen(false)}>For advertisers</Link>
                        <Link to="/faq" onClick={() => setOpen(false)}>FAQ</Link>
                        {!user && <Link to="/login" onClick={() => setOpen(false)}>Log in</Link>}
                        {user && <Link to={dashboardPath} onClick={() => setOpen(false)}>Dashboard</Link>}
                    </div>
                </div>
            )}
            {showNotif && user && (
                <div className="absolute right-4 top-16 w-80 max-h-96 overflow-auto card-base z-50" data-testid="notif-dropdown">
                    <div className="flex items-center justify-between p-4 border-b border-ink-200">
                        <span className="eyebrow">Notifications</span>
                        <button className="text-xs underline" onClick={markRead} data-testid="notif-mark-read-btn">Mark read</button>
                    </div>
                    {notifs.length === 0 && (
                        <div className="p-6 text-sm text-ink-600">No notifications yet.</div>
                    )}
                    {notifs.map((n) => (
                        <div key={n.id} className={`p-4 border-b border-ink-200 ${n.read ? "" : "bg-cream"}`} data-testid={`notif-${n.id}`}>
                            <div className="text-sm font-semibold">{n.title}</div>
                            {n.body && <div className="text-xs text-ink-600 mt-1">{n.body}</div>}
                            <div className="text-[10px] mt-1 text-ink-600 font-mono">{new Date(n.created_at).toLocaleString()}</div>
                        </div>
                    ))}
                </div>
            )}
        </header>
    );
}
